import {toLocalDate} from "./formatData";

const BOM = "\uFEFF";
const LINE_BREAK = "\r\n";
export const DEFAULT_DELIMITER = ";";
const FORMULA_CHARS = ["=", "+", "-", "@"];
const NON_EXPORTABLE_FIELDS = ["actions"];
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?)?/;

const getFieldValue = (row, field) =>
  String(field)
    .split(".")
    .reduce((acc, key) => (acc === null || acc === undefined ? undefined : acc[key]), row);

const normalizeColumns = (columns, rows = []) => {
  if (Array.isArray(columns) && columns.length > 0) {
    return columns
      .map((column) => (typeof column === "string" ? {field: column} : column))
      .filter((column) => column?.field && column.exportable !== false && !NON_EXPORTABLE_FIELDS.includes(column.field))
      .map((column) => ({
        field: column.field,
        header: column.header || column.headerName || column.field,
        format: typeof column.format === "function" ? column.format : null
      }));
  }

  const fields = [];
  rows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (!fields.includes(key) && !NON_EXPORTABLE_FIELDS.includes(key)) fields.push(key);
    });
  });

  return fields.map((field) => ({field, header: field, format: null}));
};

const stringifyValue = (value, options = {}) => {
  const {decimalComma = true, formatDates = true} = options;

  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Sí" : "No";

  if (typeof value === "number") {
    if (!isFinite(value)) return "";
    return decimalComma ? String(value).replace(".", ",") : String(value);
  }

  if (value instanceof Date) {
    if (isNaN(value.getTime())) return "";
    return formatDates ? toLocalDate(value) : value.toISOString();
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => stringifyValue(item, options))
      .filter((item) => item !== "")
      .join(" | ");
  }

  if (typeof value === "object") return JSON.stringify(value);

  const text = String(value);
  if (formatDates && ISO_DATE_REGEX.test(text)) {
    const date = new Date(text);
    if (!isNaN(date.getTime())) return toLocalDate(date);
  }

  return text;
};

const escapeCell = (text, options = {}) => {
  const {delimiter = DEFAULT_DELIMITER, sanitizeFormulas = true} = options;

  let value = String(text).replace(/\r\n|\r/g, "\n");

  // Evita que Excel interprete la celda como fórmula (CSV injection)
  if (sanitizeFormulas && FORMULA_CHARS.includes(value.charAt(0))) {
    value = `'${value}`;
  }

  const needsQuotes = value.includes(delimiter) || value.includes('"') || value.includes("\n");
  return needsQuotes ? `"${value.replace(/"/g, '""')}"` : value;
};

export const buildCsv = ({rows = [], columns, delimiter = DEFAULT_DELIMITER, includeBom = true, ...options} = {}) => {
  const data = Array.isArray(rows) ? rows : rows?.data || [];
  const cols = normalizeColumns(columns, data);
  const cellOptions = {...options, delimiter};

  const headerLine = cols.map((column) => escapeCell(column.header, cellOptions)).join(delimiter);

  const bodyLines = data.map((row) =>
    cols
      .map((column) => {
        const rawValue = getFieldValue(row, column.field);
        const value = column.format ? column.format(rawValue, row) : rawValue;
        return escapeCell(stringifyValue(value, options), cellOptions);
      })
      .join(delimiter)
  );

  return (includeBom ? BOM : "") + [headerLine, ...bodyLines].join(LINE_BREAK) + LINE_BREAK;
};

export const buildFilename = (filename = "export", withTimestamp = true) => {
  const base = String(filename).replace(/\.csv$/i, "") || "export";
  if (!withTimestamp) return `${base}.csv`;

  const now = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(
    now.getMinutes()
  )}`;

  return `${base}_${stamp}.csv`;
};

export const downloadCsv = (content, filename = "export.csv") => {
  if (typeof document === "undefined") return false;

  const blob = new Blob([content], {type: "text/csv;charset=utf-8;"});
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return true;
};

export const exportToCsv = ({rows = [], columns, filename = "export", withTimestamp = true, ...options} = {}) => {
  const data = Array.isArray(rows) ? rows : rows?.data || [];
  if (data.length === 0) return null;

  const content = buildCsv({rows: data, columns, ...options});
  const finalFilename = buildFilename(filename, withTimestamp);

  downloadCsv(content, finalFilename);

  return {filename: finalFilename, rowCount: data.length, content};
};
